import { useRef, useState } from 'react'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Calculator, Save } from 'lucide-react'
import { listStock, type StockItem } from '@/api/stock'
import { channelPreview, channelCalculate, setDraftPrice, setStrategy, type ChannelPreviewRow } from '@/api/pricing'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { ChannelPreviewTable } from './ChannelPreviewTable'
import { DraftPriceCell } from './DraftPriceCell'
import { fetchListingsByStockItemIds } from '@/api/portalListings'
import { selectPortalStatusForChannel, selectPortalDataState } from './portal-status'
import { PortalStatusBadges } from './PortalStatusBadges'
import { AmazonModelComparison } from './AmazonModelComparison'

export default function PricingWorkspace() {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [rows, setRows] = useState<ChannelPreviewRow[]>([])
  const [lastSkus, setLastSkus] = useState<string[]>([])
  const resultRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['pricing-grundartikel', search],
    // Multi-Suche: Komma trennt mehrere Sternnummern/Begriffe (ein Backend-Call pro Token,
    // Ergebnisse dedupliziert). NUR Komma als Trenner — Leerzeichen gehören zu Namenssuchen
    // ("Möbelgriff 12mm"). Ohne Komma exakt das bisherige Verhalten.
    queryFn: async () => {
      const tokens = search.split(',').map((t) => t.trim()).filter(Boolean)
      if (tokens.length <= 1) {
        return listStock({ search: search.trim() || undefined, limit: 200, is_grundartikel: true })
      }
      const results = await Promise.all(
        tokens.map((t) => listStock({ search: t, limit: 200, is_grundartikel: true })),
      )
      const seen = new Set<string>()
      const merged: StockItem[] = []
      for (const r of results) {
        for (const it of r.items) {
          if (!seen.has(it.sku)) { seen.add(it.sku); merged.push(it) }
        }
      }
      return { items: merged, has_more: results.some((r) => r.has_more) }
    },
    staleTime: 30_000,
  })
  const items: StockItem[] = data?.items ?? []

  const scrollToResult = () => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  const preview = useMutation({
    mutationFn: (skus: string[]) => channelPreview(skus),
    onSuccess: (res, skus) => {
      setRows(res.rows)
      setLastSkus(skus)
      toast.success(`${res.count} Varianten berechnet (Vorschau)`)
      scrollToResult()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Vorschau fehlgeschlagen'),
  })

  // Portal-Angebote zu den angezeigten Zeilen — bewusst entkoppelt: schlaegt der
  // Abruf fehl, bleibt die Kalkulation nutzbar und die Badges zeigen „keine Daten".
  // Wichtig: IDs kommen aus dem tatsaechlich angezeigten `rows`-State, nicht aus
  // `preview.data`, da die Tabelle auch ueber `calculate` (Berechnen & speichern
  // ohne vorherige Vorschau) oder `refreshPreview` (Strategie-Toggle) befuellt wird.
  const listingItemIds = rows
    .map((r) => r.stock_item_id)
    .filter((id): id is number => typeof id === 'number')

  const listingsEnabled = listingItemIds.length > 0

  const listings = useQuery({
    queryKey: ['portal-listings-bulk', listingItemIds],
    queryFn: () => fetchListingsByStockItemIds(listingItemIds),
    enabled: listingsEnabled,
  })

  // Je Zeile: liegen Daten vor, gilt der echte Zustand. Fehlen sie und der Abruf
  // ist (teilweise) gescheitert, wird das ehrlich als „nicht abrufbar" gezeigt —
  // sonst behauptete die Zeile „kein Eintrag", obwohl nie nachgesehen wurde.
  const portalDataStateFor = (stockItemId: number | undefined) =>
    selectPortalDataState({
      hasRowData: stockItemId != null && listings.data?.byItem[stockItemId] !== undefined,
      enabled: listingsEnabled,
      isPending: listings.isPending,
      isError: listings.isError,
      partialFailure: listings.data?.partialFailure ?? false,
    })

  const calculate = useMutation({
    mutationFn: (skus: string[]) => channelCalculate(skus),
    onSuccess: (res, skus) => {
      setRows(res.rows)
      setLastSkus(skus)
      toast.success(`Entwürfe gespeichert: ${res.updated_base}× Basis, ${res.updated_amazon}× Amazon`)
      scrollToResult()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen'),
  })

  // Stiller Refresh nach Strategie-Toggle (kein Scroll, kein Loading-Austausch der Tabelle)
  const refreshPreview = useMutation({
    mutationFn: (skus: string[]) => channelPreview(skus),
    onSuccess: (res) => setRows(res.rows),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Aktualisierung fehlgeschlagen'),
  })

  const toggleStrategy = useMutation({
    mutationFn: ({ sku, target, strategy }: { sku: string; target: 'amazon' | 'base'; strategy: 'margin' | 'break_even' }) =>
      setStrategy(sku, strategy, target),
    onSuccess: (_res, vars) => {
      toast.success(`Strategie (${vars.target}) für ${vars.sku}: ${vars.strategy === 'break_even' ? 'Break-Even' : 'Marge'}`)
      if (lastSkus.length > 0) refreshPreview.mutate(lastSkus)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Strategie fehlgeschlagen'),
  })

  async function saveDraft(sku: string, price: number, target: 'base' | 'amazon') {
    try {
      await setDraftPrice(sku, price, target)
      toast.success(`Entwurf (${target}) für ${sku} gesetzt`)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Entwurf fehlgeschlagen')
      throw e
    }
  }

  function toggle(sku: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(sku)) next.delete(sku)
      else next.add(sku)
      return next
    })
  }

  const selectedSkus = [...selected]
  const busy = preview.isPending || calculate.isPending

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-ink">Preis-Kalkulation</h1>
          <p className="text-sm text-ink-2 mt-1">
            Grundartikel auswählen und VK-Preise je Kanal berechnen (Set-EK, Versandstufe, Strategie).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={selectedSkus.length === 0 || busy}
            onClick={() => preview.mutate(selectedSkus)}
          >
            <Calculator className="size-3.5" />
            Vorschau berechnen ({selectedSkus.length})
          </Button>
          <Button
            size="sm"
            variant="default"
            className="gap-1.5"
            disabled={selectedSkus.length === 0 || busy}
            onClick={() => calculate.mutate(selectedSkus)}
          >
            <Save className="size-3.5" />
            Berechnen & speichern ({selectedSkus.length})
          </Button>
        </div>
      </header>

      <div ref={resultRef}>
        <ChannelPreviewTable
          rows={rows}
          loading={busy}
          onToggleStrategy={(r, t) =>
            toggleStrategy.mutate({ sku: r.sku, target: t.target, strategy: t.strategy === 'break_even' ? 'margin' : 'break_even' })
          }
          renderDraftCell={(r, t) => t.price === null ? null : (
            <DraftPriceCell
              key={`${r.sku}:${t.target}:${t.price}`}
              sku={r.sku}
              initial={t.price}
              onSave={(sku, price) => saveDraft(sku, price, t.target)}
            />
          )}
          renderChannelListings={(r, t) => (
            <PortalStatusBadges
              statuses={selectPortalStatusForChannel(
                (r.stock_item_id != null && listings.data?.byItem[r.stock_item_id]) || [],
                t.target,
              )}
              dataState={portalDataStateFor(r.stock_item_id)}
            />
          )}
        />
      </div>

      <AmazonModelComparison selectedSkus={selectedSkus} />

      <Input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Händler-ArtNr., SKU, Name… (mehrere mit Komma: 974, 959)"
        className="max-w-md"
      />

      <div className="rounded-md border border-line bg-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-2 text-xs uppercase text-muted">
              <th className="w-10 px-3 py-2"></th>
              <th className="text-left font-medium px-2 py-2">Grundartikel</th>
              <th className="text-left font-medium px-2 py-2">Händl.ArtNr.</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={3} className="text-center py-8 text-ink-2">Lade…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={3} className="text-center py-8 text-ink-2">Keine Grundartikel.</td></tr>
            ) : (
              items.map((p) => (
                <tr key={p.sku} className="border-b border-line-2 last:border-0 hover:bg-panel-2">
                  <td className="px-3 py-2">
                    <Checkbox
                      checked={selected.has(p.sku)}
                      onCheckedChange={() => toggle(p.sku)}
                      aria-label={`${p.sku} auswählen`}
                    />
                  </td>
                  <td className="px-2 py-2 text-ink truncate max-w-[320px]">{p.name}</td>
                  <td className="px-2 py-2 font-mono text-xs text-primary"><GrundartikelLink code={p.merchant_sku} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
